import React, { useState, useEffect, useCallback } from 'react';

import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Button, 
  Card, 
  CardContent,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import PrintIcon from '@mui/icons-material/Print';
import { PrescriptionService } from '../../services/prescriptionService';
import format from 'date-fns/format';
import { useUserData } from '../../hooks/useUserData';
import PrescriptionPrintComponent from './PrescriptionPrintComponent';


// Styled Components - Modern Theme (matching CreateEncounter)
const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(3),
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
  },
}));

const ModernButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 600,
  padding: theme.spacing(1.5, 3),
}));

const SectionCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
  marginBottom: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: '12px',
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
  border: `1px solid ${theme.palette.divider}`,
  '& .MuiTableHead-root .MuiTableRow-root .MuiTableCell-head': {
    backgroundColor: theme.palette.grey[50],
    fontWeight: 600,
    borderBottom: `2px solid ${theme.palette.divider}`,
  },
  '& .MuiTableBody-root .MuiTableRow-root:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

function PrescriptionsList() {
  const navigate = useNavigate();
  
  // Use the new useUserData hook for consistent data structure
  const { defaultFacility } = useUserData();
  
  
  const [prescriptions, setPrescriptions] = useState([]);
  const [patients, setPatients] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPrescriptions, setFilteredPrescriptions] = useState([]);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [selectedPrescription, setSelectedPrescription] = useState(null);

  const fetchPrescriptionsData = useCallback(async () => {
    try {
      setLoading(true);      
      let prescriptionsData;
        
      prescriptionsData = await PrescriptionService.getPrescriptionsByFacility(defaultFacility.id, 'active');

      const patientIds = [...new Set(prescriptionsData.map(p => p.patientId))];
      
      // Fetch patient data using service
      const patientsData = await PrescriptionService.getPatientsData(patientIds);

      setPrescriptions(prescriptionsData);
      setPatients(patientsData);
      setError(null);
    } catch (err) {
      console.error('Error fetching prescriptions:', err);
      setError('Failed to load prescriptions');
    } finally {
      setLoading(false);
    }
  }, [defaultFacility.id]);

  useEffect(() => {
    fetchPrescriptionsData();
  }, [fetchPrescriptionsData]);

  useEffect(() => {
    // Filter prescriptions based on search term
    if (!searchTerm) {
      setFilteredPrescriptions(prescriptions);
    } else {
      const filtered = prescriptions.filter(prescription => {
        const patient = patients[prescription.patientId];
        const patientName = patient ? patient.name.toLowerCase() : '';
        const medicationNames = prescription.medications.map(med => med.name.toLowerCase()).join(' ');
        const searchLower = searchTerm.toLowerCase();
        
        return patientName.includes(searchLower) || 
               medicationNames.includes(searchLower) ||
               prescription.pharmacy?.name?.toLowerCase().includes(searchLower);
      });
      setFilteredPrescriptions(filtered);
    }
  }, [searchTerm, prescriptions, patients]);

  const handlePatientClick = (patientId) => {
    navigate(`/patients/${patientId}`);
  };

  const handlePrintClick = (prescription) => {
    setSelectedPrescription(prescription);
    setPrintDialogOpen(true);
  };

  const handlePrintDialogClose = () => {
    setPrintDialogOpen(false);
    setSelectedPrescription(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'completed': return 'default';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <HeaderCard>
          <CardContent sx={{ py: 1.5, px: 3, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
              Current Prescriptions
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              Manage and view all active prescriptions
            </Typography>
          </CardContent>
        </HeaderCard>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <HeaderCard>
          <CardContent sx={{ py: 1.5, px: 3, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
              Current Prescriptions
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              Manage and view all active prescriptions
            </Typography>
          </CardContent>
        </HeaderCard>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <HeaderCard>
        <CardContent sx={{ py: 1.5, px: 3, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
            Current Prescriptions
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
            Manage and view all active prescriptions
          </Typography>
        </CardContent>
      </HeaderCard>
      
      <SectionCard>
        <Box sx={{ p: 3 }}>
          <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
            <StyledTextField
              placeholder="Search by patient name, medication, or pharmacy..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ flexGrow: 1, maxWidth: 400 }}
            />
            
            <Typography variant="body2" color="text.secondary">
              {filteredPrescriptions.length} prescription(s) found
            </Typography>
          </Box>

          {filteredPrescriptions.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <LocalPharmacyIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {searchTerm ? 'No prescriptions found' : 'No current prescriptions'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchTerm 
                  ? 'Try adjusting your search terms' 
                  : 'Prescriptions will appear here when they are created'
                }
              </Typography>
            </Box>
          ) : (
            <StyledTableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Patient</TableCell>
                    <TableCell>Medications</TableCell>
                    <TableCell>Pharmacy</TableCell>
                    <TableCell>Date Created</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredPrescriptions.map((prescription) => {
                    const patient = patients[prescription.patientId];
                    return (
                      <TableRow key={prescription.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon sx={{ color: 'text.secondary' }} />
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {patient ? (patient.firstName && patient.lastName 
                                  ? `${patient.firstName} ${patient.lastName}` 
                                  : patient.name || 'Unknown Patient') : 'Unknown Patient'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {patient ? `DOB: ${patient.dateOfBirth}` : prescription.patientId}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        
                        <TableCell>
                          <Box>
                            {prescription.medications.map((med, idx) => (
                              <Box key={idx} sx={{ mb: idx < prescription.medications.length - 1 ? 1 : 0 }}>
                                <Typography variant="body2" fontWeight="medium">
                                  {med.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {med.dosage} • {med.frequency} • {med.duration}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </TableCell>
                        
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {prescription.pharmacy?.name || 'No pharmacy'}
                            </Typography>
                            {prescription.pharmacy?.address && (
                              <Typography variant="caption" color="text.secondary">
                                {prescription.pharmacy.address}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        
              
                        
                        <TableCell>
                          <Typography variant="body2">
                            {format(new Date(prescription.createdAt), 'MMM dd, yyyy')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {format(new Date(prescription.createdAt), 'h:mm a')}
                          </Typography>
                        </TableCell>
                        
                        <TableCell>
                          <Chip 
                            label={prescription.status} 
                            color={getStatusColor(prescription.status)}
                            size="small"
                          />
                        </TableCell>
                        
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <ModernButton
                              variant="outlined"
                              size="small"
                              startIcon={<PrintIcon />}
                              onClick={() => handlePrintClick(prescription)}
                              sx={{ 
                                borderColor: 'primary.main',
                                '&:hover': {
                                  backgroundColor: 'primary.main',
                                  color: 'white'
                                }
                              }}
                            >
                              Print
                            </ModernButton>
                            <ModernButton
                              variant="outlined"
                              size="small"
                              onClick={() => handlePatientClick(prescription.patientId)}
                              disabled={!patient}
                            >
                              View Patient
                            </ModernButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </StyledTableContainer>
          )}
        </Box>
      </SectionCard>

      {/* Print Dialog */}
      {selectedPrescription && (
        <PrescriptionPrintComponent
          prescriptions={[selectedPrescription]}
          patient={patients[selectedPrescription.patientId]}
          facilityId={defaultFacility?.id}
          open={printDialogOpen}
          onClose={handlePrintDialogClose}
          onPrint={handlePrintDialogClose}
        />
      )}
    </Box>
  );
};

export default PrescriptionsList; 